/** LOGO_UPLOAD control: show the current operator logo and upload a replacement.
 *
 * The logo is required (itineraries won't save without one). Reads the current URL
 * from `item.detail.logo_url`; a staged File is uploaded via the multipart
 * `upload_logo` endpoint, which replaces the logo and records the completion run.
 */
import { Box, Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem } from '@/api/preflight';
import { ImageUploadField } from '@/components/ImageUploadField/ImageUploadField';
import type { ImageFieldValue } from '@/components/ImageUploadField/ImageUploadField';
import { useSnackbar } from '@/components/Snackbar';

export function LogoUploadControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const [staged, setStaged] = useState<ImageFieldValue>(null);
  const currentUrl = typeof item.detail.logo_url === 'string' ? item.detail.logo_url : null;
  const fullUrl = typeof item.detail.logo_full_url === 'string' ? item.detail.logo_full_url : null;

  const upload = useMutation({
    mutationFn: (file: File) => items.uploadLogo(topId, item.id, file),
    onSuccess: (run) => {
      snackbar.show(
        run.status === 'error' ? run.error_message || 'Upload failed.' : 'Logo updated.',
        run.status === 'error' ? 'error' : 'success',
      );
      setStaged(null);
      onChanged();
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Upload failed.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <ImageUploadField
        label="Operator logo"
        currentUrl={currentUrl}
        fullUrl={fullUrl}
        value={staged}
        onChange={setStaged}
        canManage={canManage}
      />
      {canManage && staged instanceof File && (
        <Button
          size="small"
          variant="contained"
          sx={{ mt: 1 }}
          disabled={upload.isPending}
          onClick={() => upload.mutate(staged)}
        >
          Upload logo
        </Button>
      )}
    </Box>
  );
}
